import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { useLogError } from '../ErrorBoundary/useLogError';
import { ChannelState } from '../../interfaces/ChannelState';
import React, { useContext, JSX, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getLastConfirmedMessage } from '../../reduxStore/selectors/messages.selector';
import { reconnectChatMessages } from './i18n';
import { instanceContext, configContext } from 'src/contexts';
import { styled } from '@mui/material/styles';

export const ReconnectChat = styled(
	({ className, ...props }: JSX.IntrinsicElements['div']) => {
		const { startConnection } = useContext(instanceContext);
		const { channelState } = useContext(configContext);
		const intl = useIntl();

		const chat__disconnected = intl.formatMessage(
			reconnectChatMessages.chat__disconnected,
		);
		const chat__reconnecting = intl.formatMessage(
			reconnectChatMessages.chat__reconnecting,
		);
		const chat__error = intl.formatMessage(reconnectChatMessages.chat__error);
		const getMessage = useCallback(
			(state: ChannelState) => {
				switch (state) {
					case ChannelState.DISCONNECTED:
						return chat__disconnected;
					case ChannelState.RECONNECTING:
						return chat__reconnecting;
					case ChannelState.ERROR:
						return chat__error;
					default:
						return '';
				}
			},
			[chat__disconnected, chat__reconnecting, chat__error],
		);
		const lastTimestamp = useSelector(getLastConfirmedMessage);

		const logError = useLogError('ReconnectChat');

		if (
			channelState === ChannelState.CHANNEL_OPEN ||
			channelState === ChannelState.CHANNEL_CLOSED ||
			!channelState
		)
			return null;

		return (
			<div
				className={classNames(
					className,
					'chat__reconnect',
					`chat__reconnect--${channelState}`,
				)}
				onClick={() => {
					if (channelState !== ChannelState.RECONNECTING) {
						try {
							startConnection(lastTimestamp);
						} catch (error: any) {
							logError('startConnection', { error });
						}
					}
				}}
				{...props}
			>
				<span>{getMessage(channelState)}</span>
			</div>
		);
	},
	{
		label: 'ReconnectChat',
		target: 'ReconnectChat-root',
	},
)({
	color: 'var(--chat__reconnect_color)',
	'&:not(.chat__reconnect--reconnecting)': {
		cursor: 'pointer',
	},
});
